import { Dialog, DialogContent } from '@ovComponents/2-component/dialog/dialog';
import { TableCell, TableRow } from '@ovComponents/2-component/table/table';
import {
  Box,
  Checkbox,
  CircularProgress,
  Tooltip,
  Typography,
  useTheme,
} from 'hephaestus/web';
import React, { useState } from 'react';
import { useFormatMoney } from '../hooks/useFormatMoney';
import {
  PORTFOLIO_SELECT_COLUMN_WIDTH,
  portfolioFixedColumnSx,
} from '../hooks/usePortfolioColumns';
import type { EnrichedPortfolioReport } from '../hooks/usePortfolioTableState.types';
import { usePortfolioTradeBlotter } from '../hooks/usePortfolioTradeBlotter';
import { TABULAR_SX, TRUNCATE_SX } from '../styles';
import { getPortfolioName } from '../utils';
import {
  formatAdvisorLabel,
  formatCustodianLabel,
  formatLastRebalDateLabel,
  formatModelNameLabel,
  formatReasonLabel,
  getAdvisorTooltipForReport,
} from '../utils/tableFormatters';
import { PortfolioCashPositionCell } from './portfolioCashPositionCell';
import PortfolioRowDetail from './portfolioRowDetail';
import TradeBlotter from './tradeBlotter';

const PortfolioRow = React.memo(
  ({
    report,
    selected,
    onToggleSelect,
    isExpanded,
    onSetExpanded,
    organizationId,
    bulkTradingProcess,
    onTradeComplete,
    isPinned = false,
    colSpan,
    canReadRepCode,
    driftTolerancePercent,
  }: {
    report: EnrichedPortfolioReport;
    selected: boolean;
    onToggleSelect: (reportId: string) => void;
    isExpanded: boolean;
    onSetExpanded: (id: string | null) => void;
    organizationId: string;
    bulkTradingProcess: string | undefined;
    onTradeComplete: () => void;
    isPinned?: boolean;
    colSpan: number;
    canReadRepCode: boolean;
    driftTolerancePercent: number;
  }) => {
    const formatMoney = useFormatMoney();
    const theme = useTheme();
    const [localOpen, setLocalOpen] = useState(false);
    const {
      showTradeBlotter,
      setShowTradeBlotter,
      openBlotter,
      blotterLoading,
      strLoading,
      blotterReport,
      activeSubTradeRequests,
      onRefreshActiveSTRs,
      onGenerateTrades,
    } = usePortfolioTradeBlotter({
      report,
      organizationId,
      bulkTradingProcess,
      onTradeComplete,
    });

    const open = localOpen || isExpanded;
    const advisorLabel = formatAdvisorLabel(report, { canReadRepCode });
    const advisorTooltip = getAdvisorTooltipForReport(report, {
      canReadRepCode,
    });

    // Parent deep-link sets `isExpanded` without toggling `localOpen`.
    const handleToggleOpen = () => {
      if (isExpanded) {
        onSetExpanded(null);
        setLocalOpen(false);
        return;
      }
      setLocalOpen((prev) => !prev);
    };

    return (
      <>
        <TableRow
          id={`portfolio-row-${report.id}`}
          pointer
          hover
          backgroundColor={isPinned ? theme.color.primaryContainer : undefined}
          style={
            isPinned
              ? {
                  outline: `1px solid ${theme.color.primary}`,
                  outlineOffset: -1,
                }
              : undefined
          }
          onClick={handleToggleOpen}
        >
          <TableCell
            sx={{ width: PORTFOLIO_SELECT_COLUMN_WIDTH, py: 0.5, px: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <Checkbox
                checked={selected}
                onChange={() => onToggleSelect(report.id)}
              />
            </Box>
          </TableCell>
          <TableCell sx={{ ...portfolioFixedColumnSx('portfolio'), py: 0.5 }}>
            <Typography
              variant='bodySmall'
              sx={{ fontWeight: 600, lineHeight: 1.3, ...TRUNCATE_SX }}
            >
              {getPortfolioName(report)}
            </Typography>
          </TableCell>
          <TableCell sx={{ ...portfolioFixedColumnSx('modelName'), py: 0.5 }}>
            <Typography variant='bodySmall' sx={TRUNCATE_SX}>
              {formatModelNameLabel(report)}
            </Typography>
          </TableCell>
          <TableCell sx={{ ...portfolioFixedColumnSx('reason'), py: 0.5 }}>
            <Typography variant='bodySmall' sx={TRUNCATE_SX}>
              {formatReasonLabel(report)}
            </Typography>
          </TableCell>
          <TableCell sx={{ ...portfolioFixedColumnSx('custodian'), py: 0.5 }}>
            <Typography variant='bodySmall' sx={TRUNCATE_SX}>
              {formatCustodianLabel(report)}
            </Typography>
          </TableCell>
          <TableCell sx={{ ...portfolioFixedColumnSx('advisor'), py: 0.5 }}>
            <Typography variant='bodySmall' sx={TRUNCATE_SX}>
              {advisorTooltip ? (
                <Tooltip placement='top' title={advisorTooltip}>
                  <span>{advisorLabel}</span>
                </Tooltip>
              ) : (
                advisorLabel
              )}
            </Typography>
          </TableCell>
          <TableCell sx={{ ...portfolioFixedColumnSx('value'), py: 0.5 }}>
            <Typography
              variant='bodySmall'
              sx={{ fontWeight: 600, ...TRUNCATE_SX, ...TABULAR_SX }}
            >
              {formatMoney(report.marketValueCents)}
            </Typography>
          </TableCell>
          <PortfolioCashPositionCell
            report={report}
            driftTolerancePercent={driftTolerancePercent}
          />
          <TableCell
            sx={{ ...portfolioFixedColumnSx('lastRebalDate'), py: 0.5 }}
          >
            <Typography variant='bodySmall' sx={TRUNCATE_SX}>
              {formatLastRebalDateLabel(report)}
            </Typography>
          </TableCell>
          <TableCell
            sx={{
              ...portfolioFixedColumnSx('trades'),
              py: 0.5,
              textAlign: 'center',
            }}
          >
            {report.pendingSubTradeRequests > 0 ? (
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  if (!open) handleToggleOpen();
                }}
                sx={{
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 0.75,
                  py: 0.25,
                  borderRadius: '6px',
                  bgcolor: theme.color.primaryVariant,
                  '&:hover': { opacity: 0.85 },
                }}
              >
                <Typography
                  variant='bodySmall'
                  sx={{ fontWeight: 600, color: theme.color.primary }}
                >
                  {report.pendingSubTradeRequests}
                </Typography>
              </Box>
            ) : (
              <Typography
                variant='labelSmall'
                sx={{ color: theme.color.onDisabled }}
              >
                —
              </Typography>
            )}
          </TableCell>
        </TableRow>

        {open && (
          <PortfolioRowDetail
            report={report}
            onShowTradeBlotter={openBlotter}
            colSpan={colSpan}
          />
        )}

        <Dialog
          open={showTradeBlotter && (blotterLoading || strLoading)}
          maxWidth='xs'
        >
          <DialogContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
              <CircularProgress size='medium' />
              <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
                Loading blotter data…
              </Typography>
            </Box>
          </DialogContent>
        </Dialog>
        {showTradeBlotter && !blotterLoading && !strLoading && (
          <TradeBlotter
            portfolioReport={blotterReport || report}
            onClose={() => setShowTradeBlotter(false)}
            activeSubTradeRequests={activeSubTradeRequests}
            onRefreshActiveSTRs={onRefreshActiveSTRs}
            onGenerateTrades={onGenerateTrades}
          />
        )}
      </>
    );
  },
);

export default PortfolioRow;
